/**
 * Utility to generate JSON-LD structured data for SEO and LLM Indexing (AI-SEO).
 */
export const useSeoSchema = () => {
	const config = useRuntimeConfig()
	const siteUrl = 'https://mycovergenius.com/'

	const generateOrganizationSchema = () => ({
		'@context': 'https://schema.org',
		'@type': 'Organization',
		'@id': `${siteUrl}#organization`,
		name: 'MyCoverGenius',
		url: siteUrl,
		logo: {
			'@type': 'ImageObject',
			url: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082945/genius-logo_ynlmlr.webp',
			width: '600',
			height: '600'
		},
		description:
			'MyCoverGenius is a leading InsurTech company in Nigeria, providing accessible and affordable insurance products through innovative technology partnerships.',
		sameAs: [
			'https://twitter.com/mycovergenius',
			'https://www.linkedin.com/company/mycovergenius/',
			'https://www.facebook.com/mycovergenius',
			'https://www.instagram.com/mycovergenius/'
		],
		knowsAbout: ['InsurTech', 'Insurance', 'Digital Claims', 'Health Insurance', 'Auto Insurance', 'Micro-insurance'],
		areaServed: {
			'@type': 'Country',
			name: 'Nigeria'
		},
		email: 'hello@mycovergenius.com'
	})

	const generateAboutPageSchema = (founders: any[]) => ({
		'@context': 'https://schema.org',
		'@type': 'AboutPage',
		'@id': `${siteUrl}about-us#webpage`,
		url: `${siteUrl}about-us`,
		name: 'About MyCoverGenius | Leading InsurTech in Nigeria',
		description:
			'Learn about the mission, values, and founders of MyCoverGenius, the insurtech making insurance simple and accessible for every Nigerian.',
		mainEntity: {
			'@type': 'Organization',
			'@id': `${siteUrl}#organization`
		},
		contributor: founders.map(founder => ({
			'@type': 'Person',
			name: founder.name,
			jobTitle: 'Founder',
			image: founder.img ? `https://res.cloudinary.com/dzhhy3ovo/image/upload${founder.img}` : undefined,
			affiliation: {
				'@type': 'Organization',
				'@id': `${siteUrl}#organization`
			}
		}))
	})

	const generateFaqSchema = (faqList: { question: string; answer: string }[]) => ({
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqList.map(faq => ({
			'@type': 'Question',
			name: faq.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: faq.answer.replace(/<[^>]*>/g, '') // Clean HTML for JSON-LD
			}
		}))
	})

	const generateProductSchema = (product: {
		name: string
		description: string
		image?: string
		url: string
		price?: string | number
	}) => ({
		'@context': 'https://schema.org',
		'@type': 'Product',
		name: product.name,
		description: product.description,
		image: product.image,
		brand: {
			'@type': 'Brand',
			name: 'MyCoverGenius'
		},
		offers: {
			'@type': 'Offer',
			url: product.url,
			priceCurrency: 'NGN',
			price: product.price || '0',
			availability: 'https://schema.org/InStock'
		}
	})

	const generateBreadcrumbSchema = (items: { name: string; item: string }[]) => ({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: items.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			item: item.item
		}))
	})

	const generateBlogSeriesSchema = (posts: any[]) => ({
		'@context': 'https://schema.org',
		'@type': 'Blog',
		'@id': `${siteUrl}blog#blog`,
		name: 'MyCoverGenius Blog',
		description: 'Stay updated with the latest in insurance, tech, and productivity.',
		url: `${siteUrl}blog`,
		publisher: {
			'@id': `${siteUrl}#organization`
		},
		blogPost: posts.map(post => ({
			'@type': 'BlogPosting',
			headline: post.title,
			url: `${siteUrl}blog/${post.slug}_${post.id}`,
			datePublished: post.date,
			image: post.media?.src
		}))
	})

	const generateBlogPostingSchema = (post: {
		title: string
		description: string
		image: string
		url: string
		datePublished: string
		dateModified: string
		articleBody?: string
		author: { name: string; url?: string; image?: string }
	}) => ({
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		headline: post.title,
		description: post.description,
		image: post.image,
		url: post.url,
		datePublished: post.datePublished,
		dateModified: post.dateModified || post.datePublished,
		articleBody: post.articleBody,
		author: {
			'@type': 'Person',
			name: post.author.name,
			url: post.author.url,
			image: post.author.image
		},
		publisher: {
			'@id': `${siteUrl}#organization`
		},
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': post.url
		}
	})

	return {
		generateOrganizationSchema,
		generateAboutPageSchema,
		generateFaqSchema,
		generateProductSchema,
		generateBreadcrumbSchema,
		generateBlogSeriesSchema,
		generateBlogPostingSchema
	}
}
